import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { getStaffRole, supabase, type StaffRole } from "./supabase";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  role: StaffRole | null;
  loading: boolean;
  mfaRequired: boolean;
  mfaFactorId: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  enrollMfa: () => Promise<{ factorId: string; qr: string; secret: string }>;
  confirmMfaEnrollment: (factorId: string, code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function readMfaState(): Promise<{ required: boolean; factorId: string | null }> {
  const { data: level } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  const { data: factors } = await supabase.auth.mfa.listFactors();
  const totp = factors?.totp?.find((factor) => factor.status === "verified");
  return {
    required: level?.currentLevel === "aal1" && level?.nextLevel === "aal2",
    factorId: totp?.id ?? null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<StaffRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  const hydrate = useCallback(async (next: Session | null) => {
    setSession(next);
    if (!next?.user) {
      setRole(null);
      setMfaRequired(false);
      setMfaFactorId(null);
      setLoading(false);
      return;
    }
    try {
      const [staffRole, mfa] = await Promise.all([
        getStaffRole(next.user.id),
        readMfaState(),
      ]);
      setRole(staffRole);
      setMfaRequired(mfa.required);
      setMfaFactorId(mfa.factorId);
    } catch (error) {
      console.error("auth hydrate failed", error);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) void hydrate(data.session);
    });
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      if (active) void hydrate(next);
    });
    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [hydrate]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setRole(null);
  }, []);

  const verifyMfa = useCallback(async (code: string) => {
    if (!mfaFactorId) throw new Error("No authenticator enrolled");
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: mfaFactorId,
      code,
    });
    if (error) throw error;
    const mfa = await readMfaState();
    setMfaRequired(mfa.required);
    setMfaFactorId(mfa.factorId);
  }, [mfaFactorId]);

  const enrollMfa = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "Flash Quiz Console",
    });
    if (error) throw error;
    if (data.type !== "totp") throw new Error("Unexpected MFA factor type");
    return {
      factorId: data.id,
      qr: data.totp.qr_code,
      secret: data.totp.secret,
    };
  }, []);

  const confirmMfaEnrollment = useCallback(async (factorId: string, code: string) => {
    const challenge = await supabase.auth.mfa.challenge({ factorId });
    if (challenge.error) throw challenge.error;
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code,
    });
    if (error) throw error;
    const mfa = await readMfaState();
    setMfaRequired(mfa.required);
    setMfaFactorId(mfa.factorId);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      role,
      loading,
      mfaRequired,
      mfaFactorId,
      signIn,
      signOut,
      verifyMfa,
      enrollMfa,
      confirmMfaEnrollment,
    }),
    [
      session,
      role,
      loading,
      mfaRequired,
      mfaFactorId,
      signIn,
      signOut,
      verifyMfa,
      enrollMfa,
      confirmMfaEnrollment,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
